// ============================================================================
// GitHub 연동 설정 탭 (CHG-046: REQ-V46-011)
// ============================================================================

import { useState, useMemo, useEffect } from 'react';
import { GitBranch, RefreshCw, Trash2, TestTube, Loader2, Plus, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useGitHubRepos,
  useDisconnectRepo,
  useTestConnection,
  useSyncRepo,
  useStatusMappings,
  useSaveStatusMappings,
} from '../../hooks/useGitHub';
import { updateProjectGitHubSettings } from '../../services/githubService';
import type { ProjectDetail, WorkflowStatusInfo } from '../../services/projectService';
import { generateBranchName } from './branchNamePreview';
import GitHubRepoConnectModal from './GitHubRepoConnectModal';

interface Props {
  project: ProjectDetail;
  isProjectManager: boolean;
}

const GITHUB_EVENTS = [
  { event: 'BRANCH_CREATED', label: '브랜치 생성' },
  { event: 'PR_OPENED', label: 'PR 생성' },
  { event: 'PR_REVIEW_REQUESTED', label: '리뷰 요청' },
  { event: 'PR_CHANGES_REQUESTED', label: '변경 요청' },
  { event: 'PR_APPROVED', label: '리뷰 승인' },
  { event: 'PR_MERGED', label: 'PR 머지' },
  { event: 'PR_CLOSED', label: 'PR 닫힘' },
] as const;

const STRATEGY_LABEL: Record<string, string> = {
  GITHUB_FLOW: 'GitHub Flow',
  GIT_FLOW: 'Git Flow',
  TRUNK_BASED: 'Trunk-Based',
  CUSTOM: 'Custom',
};

export default function GitHubSettingsTab({ project, isProjectManager }: Props) {
  const { data: repos, isLoading } = useGitHubRepos(project.id);
  const disconnectMutation = useDisconnectRepo(project.id);
  const testMutation = useTestConnection(project.id);
  const syncMutation = useSyncRepo(project.id);

  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const { data: mappingsData } = useStatusMappings(project.id);
  const saveMappingsMutation = useSaveStatusMappings(project.id);

  // Branch naming
  const [branchPattern, setBranchPattern] = useState(
    (project as ProjectDetail & { branchNamingPattern?: string }).branchNamingPattern ?? '{type}/{issueKey}-{slug}',
  );

  // Status mappings (synced from API)
  const [statusMappings, setStatusMappings] = useState<Record<string, { targetStatusId: string | null; isEnabled: boolean }>>(() => {
    const map: Record<string, { targetStatusId: string | null; isEnabled: boolean }> = {};
    for (const e of GITHUB_EVENTS) {
      map[e.event] = { targetStatusId: null, isEnabled: true };
    }
    return map;
  });

  // Sync from API data when loaded
  useEffect(() => {
    if (mappingsData?.mappings) {
      const map: Record<string, { targetStatusId: string | null; isEnabled: boolean }> = {};
      for (const m of mappingsData.mappings) {
        map[m.githubEvent] = { targetStatusId: m.targetStatusId, isEnabled: m.isEnabled };
      }
      setStatusMappings(map);
    }
  }, [mappingsData]);

  // Commit keywords
  const [commitKeywords, setCommitKeywords] = useState<Record<string, string | null>>(() => {
    const existing = (project as ProjectDetail & { commitKeywords?: Record<string, string | null> }).commitKeywords;
    return existing ?? { fixes: null, closes: null, refs: null, see: null };
  });

  const statuses = (mappingsData?.statuses ?? project.workflow.statuses) as WorkflowStatusInfo[];
  const hasRepos = (repos?.length ?? 0) > 0;

  // Branch name preview
  const previewBranchName = useMemo(
    () => generateBranchName(branchPattern, {
      issueKey: `${project.projectKey}-999`,
      title: '로그인 오류 수정',
      issueType: 'BUG',
      projectKey: project.projectKey,
    }),
    [branchPattern, project.projectKey],
  );

  const handleDisconnect = async (repoId: string, fullName: string) => {
    if (!confirm(`'${fullName}'의 연결을 해제하시겠습니까?\n기존 커밋/PR 데이터는 보존됩니다.`)) return;
    try {
      await disconnectMutation.mutateAsync(repoId);
      toast.success('Repository 연결이 해제되었습니다.');
    } catch {
      toast.error('연결 해제에 실패했습니다.');
    }
  };

  const handleTest = async (repoId: string) => {
    try {
      const result = await testMutation.mutateAsync(repoId);
      if (result.status === 'ok') {
        toast.success(`연결 성공: ${result.repoInfo?.fullName}`);
      } else {
        toast.error(`연결 실패: ${result.message}`);
      }
    } catch {
      toast.error('연결 테스트에 실패했습니다.');
    }
  };

  const handleSync = async (repoId: string) => {
    try {
      const result = await syncMutation.mutateAsync(repoId);
      toast.success(`동기화 완료: 커밋 ${result.commits}, PR ${result.pullRequests}, 브랜치 ${result.branches}`);
    } catch {
      toast.error('동기화에 실패했습니다.');
    }
  };

  const handleSaveBranchPattern = async () => {
    try {
      await updateProjectGitHubSettings(project.id, { branchNamingPattern: branchPattern });
      toast.success('브랜치 네이밍 규칙이 저장되었습니다.');
    } catch {
      toast.error('저장에 실패했습니다.');
    }
  };

  const handleSaveCommitKeywords = async () => {
    try {
      await updateProjectGitHubSettings(project.id, { commitKeywords });
      toast.success('커밋 키워드 설정이 저장되었습니다.');
    } catch {
      toast.error('저장에 실패했습니다.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Repository 연결 관리 */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Repository 연결</h3>
            <p className="text-sm text-gray-500 mt-0.5">GitHub Repository를 프로젝트에 연결합니다.</p>
          </div>
          {isProjectManager && (
            <button
              onClick={() => setIsConnectModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
            >
              <Plus className="size-4" />
              Repository 연결
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="size-6 animate-spin text-gray-400" />
          </div>
        ) : !repos || repos.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-400">
            연결된 Repository가 없습니다.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {repos.map((repo) => (
              <div key={repo.repoId} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50">
                <GitBranch className="size-5 text-gray-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">{repo.fullName}</div>
                  <div className="text-sm text-gray-500 flex items-center gap-2 mt-0.5">
                    <span>{STRATEGY_LABEL[repo.branchStrategy] ?? repo.branchStrategy}</span>
                    <span>·</span>
                    <span>{repo.defaultBranch}</span>
                    <span>·</span>
                    <span>{repo._count.commits} commits, {repo._count.pullRequests} PRs</span>
                  </div>
                </div>
                {isProjectManager && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleTest(repo.repoId)}
                      disabled={testMutation.isPending}
                      className="p-1.5 text-gray-400 hover:text-blue-600 rounded transition-colors"
                      title="연결 테스트"
                    >
                      <TestTube className="size-4" />
                    </button>
                    <button
                      onClick={() => handleSync(repo.repoId)}
                      disabled={syncMutation.isPending}
                      className="p-1.5 text-gray-400 hover:text-green-600 rounded transition-colors"
                      title="수동 동기화"
                    >
                      <RefreshCw className={`size-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                      onClick={() => handleDisconnect(repo.repoId, repo.fullName)}
                      disabled={disconnectMutation.isPending}
                      className="p-1.5 text-gray-400 hover:text-red-500 rounded transition-colors"
                      title="연결 해제"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 브랜치 네이밍 규칙 */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">브랜치 네이밍 규칙</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              이슈에서 브랜치 생성 시 사용할 패턴을 설정합니다.
            </p>
          </div>
          <div>
            <input
              value={branchPattern}
              onChange={(e) => setBranchPattern(e.target.value)}
              disabled={!isProjectManager}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500 font-mono disabled:bg-gray-50"
            />
            <div className="flex flex-wrap gap-2 mt-2">
              {['{type}', '{issueKey}', '{slug}', '{projectKey}'].map((v) => (
                <span key={v} className="px-2 py-0.5 text-sm bg-gray-100 text-gray-600 rounded font-mono">{v}</span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Eye className="size-4 text-gray-400" />
            <span className="text-sm text-gray-500">미리보기:</span>
            <code className="text-sm bg-gray-100 px-2 py-0.5 rounded text-gray-700">{previewBranchName}</code>
          </div>
          {isProjectManager && (
            <button onClick={handleSaveBranchPattern} className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700">
              저장
            </button>
          )}
        </div>
      </div>

      {/* 상태 전환 매핑 — Repo가 1개 이상일 때만 표시 */}
      {hasRepos && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">상태 전환 매핑</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                GitHub 이벤트 발생 시 이슈 상태를 자동으로 변경합니다.
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-4 py-2 text-sm font-medium text-gray-500">GitHub 이벤트</th>
                    <th className="text-left px-4 py-2 text-sm font-medium text-gray-500">대상 상태</th>
                    <th className="text-center px-4 py-2 text-sm font-medium text-gray-500 w-16">활성</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {GITHUB_EVENTS.map(({ event, label }) => (
                    <tr key={event}>
                      <td className="px-4 py-2 text-gray-700">{label}</td>
                      <td className="px-4 py-2">
                        <StatusSelect
                          statuses={statuses}
                          value={statusMappings[event]?.targetStatusId ?? ''}
                          onChange={(val) => setStatusMappings((m) => ({ ...m, [event]: { ...m[event], targetStatusId: val || null } }))}
                          disabled={!isProjectManager}
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={statusMappings[event]?.isEnabled ?? true}
                          onChange={(e) => setStatusMappings((m) => ({ ...m, [event]: { ...m[event], isEnabled: e.target.checked } }))}
                          disabled={!isProjectManager}
                          className="rounded text-primary-600"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {isProjectManager && (
              <button
                onClick={async () => {
                  try {
                    const mappingsArray = Object.entries(statusMappings).map(([githubEvent, m]) => ({
                      githubEvent,
                      targetStatusId: m.targetStatusId,
                      isEnabled: m.isEnabled,
                    }));
                    await saveMappingsMutation.mutateAsync(mappingsArray);
                    toast.success('상태 전환 매핑이 저장되었습니다.');
                  } catch {
                    toast.error('저장에 실패했습니다.');
                  }
                }}
                disabled={saveMappingsMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {saveMappingsMutation.isPending && <Loader2 className="size-4 animate-spin" />}
                저장
              </button>
            )}
          </div>
        </div>
      )}

      {/* 커밋 키워드 매핑 */}
      {hasRepos && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">커밋 키워드 매핑</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                커밋 메시지의 키워드로 이슈 상태를 변경합니다.
              </p>
            </div>
            <div className="space-y-2">
              {(['fixes', 'closes'] as const).map((keyword) => (
                <div key={keyword} className="flex items-center gap-3">
                  <code className="w-16 text-sm font-medium text-gray-700">{keyword}</code>
                  <span className="text-gray-400">→</span>
                  <StatusSelect
                    statuses={statuses}
                    value={commitKeywords[keyword] ?? ''}
                    onChange={(val) => setCommitKeywords((k) => ({ ...k, [keyword]: val || null }))}
                    disabled={!isProjectManager}
                  />
                </div>
              ))}
              {(['refs', 'see'] as const).map((keyword) => (
                <div key={keyword} className="flex items-center gap-3">
                  <code className="w-16 text-sm font-medium text-gray-700">{keyword}</code>
                  <span className="text-gray-400">→</span>
                  <span className="text-sm text-gray-500 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200">연결만 (상태 변경 없음)</span>
                </div>
              ))}
            </div>
            {isProjectManager && (
              <button onClick={handleSaveCommitKeywords} className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700">
                저장
              </button>
            )}
          </div>
        </div>
      )}

      {/* 리뷰 SLA 설정 (CHG-047) */}
      {hasRepos && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">코드 리뷰 SLA</h3>
              <p className="text-sm text-gray-500 mt-0.5">리뷰 요청 후 첫 리뷰까지 허용되는 최대 시간을 설정합니다.</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={720}
                value={(project as ProjectDetail & { reviewSlaHours?: number }).reviewSlaHours ?? 24}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val >= 1 && val <= 720 && isProjectManager) {
                    updateProjectGitHubSettings(project.id, { reviewSlaHours: val } as Record<string, unknown>).then(() => {
                      toast.success('SLA가 저장되었습니다.');
                    }).catch(() => toast.error('저장에 실패했습니다.'));
                  }
                }}
                disabled={!isProjectManager}
                className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
              />
              <span className="text-sm text-gray-600">시간</span>
            </div>
          </div>
        </div>
      )}

      {/* Connect Modal */}
      <GitHubRepoConnectModal
        isOpen={isConnectModalOpen}
        onClose={() => setIsConnectModalOpen(false)}
        projectId={project.id}
      />
    </div>
  );
}

// --- Status Dropdown ---
function StatusSelect({
  statuses,
  value,
  onChange,
  disabled,
}: {
  statuses: WorkflowStatusInfo[];
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50 min-w-[140px]"
    >
      <option value="">없음</option>
      {statuses.map((s) => (
        <option key={s.statusId} value={s.statusId}>
          {s.name}
        </option>
      ))}
    </select>
  );
}
